import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-label", "accessibilityLabel", "colorToken", "colorTokenArgs", "filled", "id", "size", "testID", "UNSAFE_svgProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useMemo } from 'react';
import { useStyleSheet } from '@bubble-ui/hook-use-stylesheet';
import { Group } from '@bubble-ui/primitive-group';
import { Shape } from '@bubble-ui/primitive-shape';
import { Surface } from '@bubble-ui/primitive-surface';
import { withProvisionalOverrideFromContext } from '@bubble-ui/hoc-with-provisional-override-from-context';
import { usePropsSpreadingDeprecationWarning } from '@bubble-ui/hook-use-props-spreading-deprecation-warning';
import { usePropDeprecationWarning } from '@bubble-ui/hook-use-prop-deprecation-warning';
import { useDesignVersion } from '@bubble-ui/provider-design-version';
import { usePropsFromContext } from '@bubble-ui/provider-props-context';
import { useDesignContext } from '@klarna/designtokens';
import { SHOW_USAGE_WARNINGS } from '@bubble-dev/build-config';
import { defaultComponentTokens } from './defaultComponentTokens';
import { iconDefaultStyleSheetGetter } from './defaultStylesheets';
export var smallActionIconShapeSize = {
  height: 12,
  width: 12
};

// Height and width should match the icons in figma
// They are not necessarily the same as 'icon/size'
export var defaultShapeSize = {
  height: 20,
  width: 20
};

/**
 * @deprecated removed with v8.0.0
 */
export var defaultNavigationIconShapeSize = {
  height: 30,
  width: 30
};
export var smallActionIconGroupProps = {
  fill: 'none',
  fillRule: 'evenodd'
};
export var defaultAlignedActionIconGroupProps = {
  fill: 'none',
  fillRule: 'evenodd',
  x: 4,
  y: 4
};

/**
 * @deprecated removed with v8.0.0
 */
export var defaultNavigationIconGroupProps = {
  fill: 'none',
  fillRule: 'evenodd'
};
export var defaultIconGroupProps = {
  fill: 'none',
  fillRule: 'evenodd'
};
export function baseIcon(displayName, config) {
  var defaultStyleSheetGetter = (config === null || config === void 0 ? void 0 : config.defaultStyleSheetGetter) || iconDefaultStyleSheetGetter;
  var OverridableIcon = props => {
    var {
        'aria-label': ariaLabel,
        accessibilityLabel: ariaLabelDeprecated,
        colorToken,
        colorTokenArgs,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        filled,
        id,
        size: sizeProp,
        testID,
        UNSAFE_svgProps
        /**
         * @deprecated props spreading is outdated and will be removed with v7.0.0
         */
      } = props,
      restOfProps = _objectWithoutProperties(props, _excluded);
    var designVersion = useDesignVersion();
    var designContext = useDesignContext(defaultComponentTokens);
    var iconPropsFromContext = usePropsFromContext('Icon');
    var shapeSizeFromConfig = useMemo(() => config !== null && config !== void 0 && config.shapeSize ? typeof config.shapeSize === 'function' ? config.shapeSize(_objectSpread(_objectSpread({}, props), {}, {
      designVersion
    })) : config.shapeSize : undefined, [props, designVersion]);
    var shapeSize = useMemo(() => _objectSpread(_objectSpread({}, defaultShapeSize), shapeSizeFromConfig), [shapeSizeFromConfig]);
    usePropDeprecationWarning({
      componentName: 'baseIcon',
      propName: 'config.shapeSize.height',
      shouldWarn: (shapeSizeFromConfig === null || shapeSizeFromConfig === void 0 ? void 0 : shapeSizeFromConfig.height) !== undefined,
      useInstead: 'config.shapeSize.viewBoxHeight',
      removalDate: 'v8.0.0'
    });
    usePropDeprecationWarning({
      componentName: 'baseIcon',
      propName: 'config.shapeSize.width',
      shouldWarn: (shapeSizeFromConfig === null || shapeSizeFromConfig === void 0 ? void 0 : shapeSizeFromConfig.width) !== undefined,
      useInstead: 'config.shapeSize.viewBoxWidth',
      removalDate: 'v8.0.0'
    });
    var tokenOverrideSize = useMemo(() => (shapeSize === null || shapeSize === void 0 ? void 0 : shapeSize.tokenOverrideHeight) && (shapeSize === null || shapeSize === void 0 ? void 0 : shapeSize.tokenOverrideWidth) && {
      height: shapeSize.tokenOverrideHeight,
      width: shapeSize.tokenOverrideWidth
    }, [shapeSize]);
    var size = useMemo(() => sizeProp !== undefined ? {
      height: sizeProp,
      width: sizeProp
    } : tokenOverrideSize ? tokenOverrideSize : {
      height: designContext.get('icon/size').value(),
      width: designContext.get('icon/size').value()
    }, [sizeProp, tokenOverrideSize, designContext]);
    useEffect(() => {
      if (SHOW_USAGE_WARNINGS && size.height > 72) {
        console.warn("BUBBLE-UI USAGE WARNING: Icons shouldn\u2019t be scaled above 72px.");
      }
    }, [size]);
    var groupProps = config !== null && config !== void 0 && config.groupProps ? typeof config.groupProps === 'function' ? config.groupProps(props) : config.groupProps : {};
    var path = config.getPath(_objectSpread(_objectSpread({}, props), {}, {
      designVersion
    }));
    var styleSheetProps = config.styleSheetPropsGetter ? config.styleSheetPropsGetter(_objectSpread(_objectSpread({}, props), {}, {
      designContext
    })) : {
      colorToken: colorToken || iconPropsFromContext.colorToken,
      colorTokenArgs: colorTokenArgs || iconPropsFromContext.colorTokenArgs,
      designContext
    };
    var cleanRestOfProps = config !== null && config !== void 0 && config.restOfPropsSanitizer ? config.restOfPropsSanitizer(restOfProps) : _objectSpread({}, restOfProps);
    usePropsSpreadingDeprecationWarning({
      componentName: displayName,
      props: cleanRestOfProps,
      useInstead: 'UNSAFE_svgProps',
      removalDate: 'v7.0.0'
    });

    // ⚠️ THIS COMPONENT SUPPORTS STYLE SHEET OVERRIDES ⚠️
    // Keep passing all props to the style sheet, even if we don't use them ourselves
    var styleSheet = useStyleSheet(OverridableIcon, defaultStyleSheetGetter, styleSheetProps);
    return /*#__PURE__*/React.createElement(Surface, _extends({
      id: id
    }, testID ? {
      testID
    } : {}, UNSAFE_svgProps, {
      "aria-label": ariaLabel || ariaLabelDeprecated,
      viewBox: "0 0 ".concat(shapeSize.viewBoxWidth || shapeSize.width, " ").concat(shapeSize.viewBoxHeight || shapeSize.height),
      height: size.height,
      width: size.width
    }, cleanRestOfProps), /*#__PURE__*/React.createElement(Group, groupProps, /*#__PURE__*/React.createElement(Shape, _extends({
      d: path
    }, styleSheet === null || styleSheet === void 0 ? void 0 : styleSheet.pathStyle))));
  };
  OverridableIcon.displayName = displayName;
  var Icon = withProvisionalOverrideFromContext(OverridableIcon);
  return Icon;
}
//# sourceMappingURL=baseIcon.js.map